import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Trash2, Plus, CalendarX, TrendingUp, Clock, ShieldPlus, Brush, Loader2, Gift, Star, Layers } from "lucide-react";
import { Card, Btn, Input, Field } from "@/components/admin/ui";
import { API } from "@/config";

const uid = () => `srg_${Math.random().toString(36).slice(2, 10)}`;

/* ==================== Admin: Booking Settings ==================== */
/* One page for admin-configurable pricing / availability controls:
   - Blackout / vacation days (block delivery + pickup on selected dates)
   - Surge pricing windows (percentage markup for date ranges)
   - Last-minute booking controls (on/off toggle, threshold hours, flat fee)
   - Damage Protection Plan price ($20 default)                           */
export const BookingSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [blackoutInput, setBlackoutInput] = useState("");
  const [surgeWindows, setSurgeWindows] = useState([]);
  const [lastMinuteEnabled, setLastMinuteEnabled] = useState(true);
  const [lastMinuteHours, setLastMinuteHours] = useState(3);
  const [lastMinuteFee, setLastMinuteFee] = useState(0);
  const [lastMinuteTiers, setLastMinuteTiers] = useState([]);
  const [damageProtectionPrice, setDamageProtectionPrice] = useState(20);
  const [carePercent, setCarePercent] = useState(8);
  const [careMinimum, setCareMinimum] = useState(5);
  const [pomPercent, setPomPercent] = useState(10);
  const [pomMinDaysOut, setPomMinDaysOut] = useState(7);
  const [byobEnabled, setByobEnabled] = useState(false);
  const [byobPercent, setByobPercent] = useState(5);
  const [byobMinUnits, setByobMinUnits] = useState(3);
  const [byobCategoryIds, setByobCategoryIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [janitorRunning, setJanitorRunning] = useState(false);
  const [janitorResult, setJanitorResult] = useState(null);
  // Admin notifications — multi-value lists
  const [adminEmails, setAdminEmails] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [adminEmailEnabled, setAdminEmailEnabled] = useState(true);
  const [testEmailBusy, setTestEmailBusy] = useState(false);
  const [adminPhones, setAdminPhones] = useState([]);
  const [newAdminPhone, setNewAdminPhone] = useState("");
  const [adminSmsEnabled, setAdminSmsEnabled] = useState(true);
  const [testSmsBusy, setTestSmsBusy] = useState(false);
  // Finance reserves
  const [federalTaxReservePercent, setFederalTaxReservePercent] = useState(25);
  const [payoutSweepEnabled, setPayoutSweepEnabled] = useState(false);
  // Customer Reviews
  const [reviewUrl, setReviewUrl] = useState("");
  const [savingReviewUrl, setSavingReviewUrl] = useState(false);

  const sendTestEmail = async () => {
    setTestEmailBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/test-notification-email`);
      const all = data.sent_to_all || [data.sent_to];
      toast.success(`Test email sent to ${all.join(", ")} ✓`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test email failed — check the Resend API key");
    } finally {
      setTestEmailBusy(false);
    }
  };

  const sendTestSms = async () => {
    setTestSmsBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/test-notification-sms`);
      const all = Array.isArray(data.sent_to) ? data.sent_to : [data.sent_to];
      toast.success(`Test SMS sent to ${all.join(", ")} ✓`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test SMS failed — check Twilio config or phone numbers");
    } finally {
      setTestSmsBusy(false);
    }
  };

  const runJanitor = async () => {
    if (!window.confirm("Cancel all unpaid holds older than 48 hours? This releases their reserved inventory.")) return;
    setJanitorRunning(true);
    setJanitorResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/pending-janitor/run`);
      setJanitorResult(data);
      if (data.cancelled_count === 0) toast.success("Nothing to clean — no pending holds >48h old.");
      else toast.success(`Cleaned up ${data.cancelled_count} stale hold${data.cancelled_count === 1 ? "" : "s"} · freed ${data.freed_units} unit${data.freed_units === 1 ? "" : "s"} ✓`);
    } catch (e) { toast.error(e.response?.data?.detail || "Janitor failed"); }
    finally { setJanitorRunning(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, catRes, siteRes] = await Promise.all([
        axios.get(`${API}/admin/settings`),
        axios.get(`${API}/admin/categories`),
        axios.get(`${API}/site-content`),
      ]);
      setCategories(catRes.data.categories || []);
      setBlackoutDates(data.blackout_dates || []);
      setSurgeWindows(data.surge_windows || []);
      setLastMinuteEnabled(!!data.last_minute_enabled);
      setLastMinuteHours(Number(data.last_minute_threshold_hours ?? 3));
      setLastMinuteFee(Number(data.last_minute_fee ?? 0));
      setLastMinuteTiers((data.last_minute_tiers || []).map((t) => ({ ...t, id: t.id || uid() })));
      setDamageProtectionPrice(Number(data.damage_protection_price ?? 20));
      setCarePercent(Number(data.enchanted_care_percent ?? 8));
      setCareMinimum(Number(data.enchanted_care_minimum ?? 5));
      setPomPercent(Number(data.enchanted_pom_percent ?? 10));
      setPomMinDaysOut(Number(data.enchanted_pom_min_days_out ?? 7));
      setByobEnabled(!!data.byob_enabled);
      setByobPercent(Number(data.byob_percent ?? 5));
      setByobMinUnits(Number(data.byob_min_units ?? 3));
      setByobCategoryIds(data.byob_category_ids || []);
      setAdminEmailEnabled(data.admin_email_notifications_enabled !== false);
      // Populate multi-value list, falling back to legacy single field
      const loadedEmails = data.notify_admin_emails?.length
        ? data.notify_admin_emails
        : data.notify_admin_email ? [data.notify_admin_email] : [];
      setAdminEmails(loadedEmails);
      setAdminSmsEnabled(data.admin_sms_notifications_enabled !== false);
      const loadedPhones = data.notify_admin_phones?.length
        ? data.notify_admin_phones
        : data.notify_admin_phone ? [data.notify_admin_phone] : [];
      setAdminPhones(loadedPhones);
      setFederalTaxReservePercent(Number(data.federal_tax_reserve_percent ?? 25));
      setPayoutSweepEnabled(!!data.payout_sweep_enabled);
      // Load review URL from site_content
      const siteContent = siteRes.data?.content || {};
      setReviewUrl(siteContent.review_url || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load settings.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addBlackoutDate = () => {
    const v = (blackoutInput || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) { toast.error("Use YYYY-MM-DD."); return; }
    if (blackoutDates.includes(v)) { toast.error("Date already added."); return; }
    setBlackoutDates((prev) => [...prev, v].sort());
    setBlackoutInput("");
  };
  const removeBlackout = (d) => setBlackoutDates((prev) => prev.filter((x) => x !== d));

  const addSurge = () => setSurgeWindows((prev) => [
    ...prev,
    { id: uid(), name: "", start_date: "", end_date: "", percentage: 10 },
  ]);
  const updateSurge = (id, patch) =>
    setSurgeWindows((prev) => prev.map((w) => (w.id === id ? { ...w, ...patch } : w)));
  const removeSurge = (id) => setSurgeWindows((prev) => prev.filter((w) => w.id !== id));

  const canSave = useMemo(() => {
    for (const w of surgeWindows) {
      if (!w.name?.trim() || !w.start_date || !w.end_date) return false;
      if (w.start_date > w.end_date) return false;
      if (Number(w.percentage) < 0) return false;
    }
    for (const t of lastMinuteTiers) {
      const mn = Number(t.min_hours);
      const mx = Number(t.max_hours);
      const fee = Number(t.fee);
      if (isNaN(mn) || isNaN(mx) || isNaN(fee)) return false;
      if (mx <= mn || mn < 0 || fee < 0) return false;
    }
    if (Number(lastMinuteHours) < 0) return false;
    if (Number(lastMinuteFee) < 0) return false;
    if (Number(damageProtectionPrice) < 0) return false;
    if (Number(carePercent) < 0 || Number(careMinimum) < 0) return false;
    if (Number(pomPercent) < 0 || Number(pomMinDaysOut) < 0) return false;
    if (Number(byobPercent) < 0 || Number(byobPercent) > 100 || Number(byobMinUnits) < 1) return false;
    return true;
  }, [surgeWindows, lastMinuteTiers, lastMinuteHours, lastMinuteFee, damageProtectionPrice, carePercent, careMinimum, pomPercent, pomMinDaysOut, byobPercent, byobMinUnits]);

  const save = async () => {
    if (!canSave) { toast.error("Please fix highlighted fields before saving."); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        blackout_dates: blackoutDates,
        surge_windows: surgeWindows.map((w) => ({
          id: w.id, name: w.name.trim(),
          start_date: w.start_date, end_date: w.end_date,
          percentage: Number(w.percentage) || 0,
        })),
        last_minute_enabled: !!lastMinuteEnabled,
        last_minute_threshold_hours: Number(lastMinuteHours) || 0,
        last_minute_fee: Number(lastMinuteFee) || 0,
        last_minute_tiers: lastMinuteTiers.map((t) => ({
          id: t.id,
          min_hours: Number(t.min_hours) || 0,
          max_hours: Number(t.max_hours) || 0,
          fee: Number(t.fee) || 0,
        })),
        damage_protection_price: Number(damageProtectionPrice) || 0,
        enchanted_care_percent: Number(carePercent) || 0,
        enchanted_care_minimum: Number(careMinimum) || 0,
        enchanted_pom_percent: Number(pomPercent) || 0,
        enchanted_pom_min_days_out: Number(pomMinDaysOut) || 0,
        byob_enabled: !!byobEnabled,
        byob_percent: Number(byobPercent) || 0,
        byob_min_units: Number(byobMinUnits) || 1,
        byob_category_ids: byobCategoryIds,
        notify_admin_emails: adminEmails,
        admin_email_notifications_enabled: !!adminEmailEnabled,
        notify_admin_phones: adminPhones,
        admin_sms_notifications_enabled: !!adminSmsEnabled,
        federal_tax_reserve_percent: Number(federalTaxReservePercent) || 25,
        payout_sweep_enabled: !!payoutSweepEnabled,
      });
      toast.success("Settings saved.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const saveReviewUrl = async () => {
    setSavingReviewUrl(true);
    try {
      await axios.patch(`${API}/admin/site-content/review_url`, { value: reviewUrl.trim() });
      toast.success("Google review link saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally { setSavingReviewUrl(false); }
  };

  if (loading) return <div className="text-[#B9C8D5]">Loading settings…</div>;

  return (
    <div className="space-y-6" data-testid="admin-booking-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-[#FFD166]">Booking Settings</h1>
          <p className="text-sm text-[#B9C8D5]">Blackout days, holiday surge pricing, last-minute cutoff, and add-ons.</p>
        </div>
        <Btn onClick={save} disabled={saving || !canSave} data-testid="settings-save-btn">
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
        </Btn>
      </div>

      {/* ---------- BLACKOUT DAYS ---------- */}
      <Card>
        <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <CalendarX className="w-4 h-4 text-[#FFD166]" />
          <h2 className="font-serif text-xl text-[#FFD166]">Blackout / Vacation Days</h2>
        </div>
        <p className="text-xs text-[#B9C8D5] mb-4">Days you're closed. Customers can't book a trip that overlaps these dates.</p>
        <div className="flex gap-2 mb-3">
          <Input
            type="date"
            value={blackoutInput}
            onChange={(e) => setBlackoutInput(e.target.value)}
            data-testid="blackout-date-input"
          />
          <Btn onClick={addBlackoutDate} data-testid="blackout-add-btn">
            <Plus className="w-3.5 h-3.5" /> Add
          </Btn>
        </div>
        {blackoutDates.length === 0 ? (
          <p className="text-xs text-[#B9C8D5]/60 italic">No blackout days set.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="blackout-list">
            {blackoutDates.map((d) => (
              <span key={d} className="inline-flex items-center gap-2 rounded-full border border-[#F4A93A]/40 bg-[#F4A93A]/10 px-3 py-1 text-xs text-[#F4A93A]" data-testid={`blackout-chip-${d}`}>
                {d}
                <button type="button" onClick={() => removeBlackout(d)} className="hover:text-red-400" aria-label={`Remove ${d}`} data-testid={`blackout-remove-${d}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        </div>
      </Card>

      {/* ---------- SURGE PRICING ---------- */}
      <Card>
        <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#FFD166]" />
            <h2 className="font-serif text-xl text-[#FFD166]">Surge Pricing Windows</h2>
          </div>
          <Btn variant="ghost" onClick={addSurge} data-testid="surge-add-btn">
            <Plus className="w-3.5 h-3.5" /> Add window
          </Btn>
        </div>
        <p className="text-xs text-[#B9C8D5] mb-4">
          Apply a percentage markup to rental prices for date ranges (e.g. Christmas Week +10%). Applies when any night of the booking falls in the window. Delivery, tax, and add-ons are not surged.
        </p>
        {surgeWindows.length === 0 ? (
          <p className="text-xs text-[#B9C8D5]/60 italic">No surge windows configured.</p>
        ) : (
          <div className="space-y-3" data-testid="surge-list">
            {surgeWindows.map((w) => (
              <div key={w.id} className="grid md:grid-cols-[2fr_1fr_1fr_0.7fr_auto] gap-2 items-end border border-[#FFD166]/15 rounded-lg p-3 bg-black/25" data-testid={`surge-row-${w.id}`}>
                <Field label="Name">
                  <Input value={w.name} onChange={(e) => updateSurge(w.id, { name: e.target.value })} placeholder="Christmas Week" data-testid={`surge-name-${w.id}`} />
                </Field>
                <Field label="Start date">
                  <Input type="date" value={w.start_date} onChange={(e) => updateSurge(w.id, { start_date: e.target.value })} data-testid={`surge-start-${w.id}`} />
                </Field>
                <Field label="End date">
                  <Input type="date" value={w.end_date} onChange={(e) => updateSurge(w.id, { end_date: e.target.value })} data-testid={`surge-end-${w.id}`} />
                </Field>
                <Field label="Markup %">
                  <Input type="number" step="0.5" min="0" value={w.percentage} onChange={(e) => updateSurge(w.id, { percentage: e.target.value })} data-testid={`surge-pct-${w.id}`} />
                </Field>
                <Btn variant="ghost" onClick={() => removeSurge(w.id)} data-testid={`surge-remove-${w.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Btn>
              </div>
            ))}
          </div>
        )}
        </div>
      </Card>

      {/* ---------- LAST-MINUTE BOOKING ---------- */}
      <Card>
        <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-[#FFD166]" />
          <h2 className="font-serif text-xl text-[#FFD166]">Last-Minute Bookings</h2>
        </div>
        <label className="flex items-center gap-3 mb-4 cursor-pointer" data-testid="last-minute-toggle-wrap">
          <input
            type="checkbox"
            checked={lastMinuteEnabled}
            onChange={(e) => setLastMinuteEnabled(e.target.checked)}
            className="w-4 h-4 accent-[#FFD166]"
            data-testid="last-minute-enabled-toggle"
          />
          <span className="text-sm text-[#F6F1E4]">Accept last-minute bookings</span>
          <span className="text-[11px] text-[#B9C8D5]/70">
            Turn off if you're unavailable to run same-day deliveries.
          </span>
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Cutoff (hours before delivery)">
            <Input type="number" min="0" step="1" value={lastMinuteHours} onChange={(e) => setLastMinuteHours(e.target.value)} data-testid="last-minute-hours-input" />
          </Field>
          <Field label="Flat last-minute fee ($) — overridden by tiers below">
            <Input type="number" min="0" step="0.01" value={lastMinuteFee} onChange={(e) => setLastMinuteFee(e.target.value)} data-testid="last-minute-fee-input" />
          </Field>
        </div>
        <p className="text-[11px] text-[#B9C8D5]/70 mt-3">
          Bookings scheduled within the cutoff are {lastMinuteEnabled ? "accepted" : "rejected"}
          {lastMinuteEnabled && Number(lastMinuteFee) > 0 && lastMinuteTiers.length === 0 ? ` with a $${Number(lastMinuteFee).toFixed(2)} flat fee` : ""}.
          {lastMinuteTiers.length > 0 ? " Tiered surcharges are active — they override the flat fee." : ""}
        </p>
        </div>
      </Card>

      {/* ---------- LAST-MINUTE SURCHARGE TIERS ---------- */}
      <Card>
        <div className="p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#FFD166]" />
            <h2 className="font-serif text-xl text-[#FFD166]">Last-Minute Surcharge Tiers</h2>
          </div>
          <Btn
            size="sm"
            onClick={() => setLastMinuteTiers((p) => [...p, { id: uid(), min_hours: "", max_hours: "", fee: "" }])}
            data-testid="lm-tier-add-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Tier
          </Btn>
        </div>
        <p className="text-xs text-[#B9C8D5] mb-4">
          When tiers are configured, the matching fee is automatically added at checkout based on how far in advance the booking is made.
          Tiers override the flat fee above. Leave empty to use the flat fee (or no surcharge at all).
        </p>

        {lastMinuteTiers.length === 0 ? (
          <p className="text-[11px] text-[#B9C8D5]/60 italic">No tiers configured — click "Add Tier" to create one.</p>
        ) : (
          <div className="space-y-2">
            {/* Header row */}
            <div className="hidden sm:grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-[10px] uppercase tracking-wider text-[#B9C8D5]/60 px-1">
              <span>From (hrs, inclusive)</span>
              <span>To (hrs, exclusive)</span>
              <span>Surcharge ($)</span>
              <span />
            </div>
            {lastMinuteTiers.map((tier, i) => {
              const mn = Number(tier.min_hours);
              const mx = Number(tier.max_hours);
              const fee = Number(tier.fee);
              const tierInvalid = isNaN(mn) || isNaN(mx) || isNaN(fee) || mx <= mn || mn < 0 || fee < 0;
              return (
              <div key={tier.id} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`lm-tier-row-${i}`}>
                <Input
                  type="number" min="0" step="0.5"
                  placeholder="0"
                  value={tier.min_hours}
                  onChange={(e) => setLastMinuteTiers((p) => p.map((t) => t.id === tier.id ? { ...t, min_hours: e.target.value } : t))}
                  className={tierInvalid ? "!border-red-500/70" : ""}
                  data-testid={`lm-tier-min-${i}`}
                />
                <Input
                  type="number" min="0" step="0.5"
                  placeholder="e.g. 2"
                  value={tier.max_hours}
                  onChange={(e) => setLastMinuteTiers((p) => p.map((t) => t.id === tier.id ? { ...t, max_hours: e.target.value } : t))}
                  className={tierInvalid ? "!border-red-500/70" : ""}
                  data-testid={`lm-tier-max-${i}`}
                />
                <Input
                  type="number" min="0" step="0.01"
                  placeholder="$0.00"
                  value={tier.fee}
                  onChange={(e) => setLastMinuteTiers((p) => p.map((t) => t.id === tier.id ? { ...t, fee: e.target.value } : t))}
                  className={tierInvalid ? "!border-red-500/70" : ""}
                  data-testid={`lm-tier-fee-${i}`}
                />
                <button
                  type="button"
                  onClick={() => setLastMinuteTiers((p) => p.filter((t) => t.id !== tier.id))}
                  className="p-1.5 rounded text-[#B9C8D5]/50 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                  data-testid={`lm-tier-remove-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              );
            })}
            <p className="text-[10px] text-[#B9C8D5]/50 pt-1">
              Example: 0 → 2 hrs = $40, 2 → 6 hrs = $30, 6 → 15 hrs = $15. A booking 1.5 hrs before delivery would charge $40.
            </p>
          </div>
        )}
        </div>
      </Card>

      {/* ---------- DAMAGE PROTECTION PRICE (LEGACY) ---------- */}
      <Card>
        <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldPlus className="w-4 h-4 text-[#FFD166]" />
          <h2 className="font-serif text-xl text-[#FFD166]">Damage Protection Plan <span className="text-xs text-[#B9C8D5]">(legacy)</span></h2>
        </div>
        <p className="text-xs text-[#B9C8D5] mb-4">
          Legacy $20 flat add-on retained for historic reservations. New bookings use Enchanted Care instead. Leave at $0 to hide the toggle everywhere.
        </p>
        <div className="max-w-xs">
          <Field label="Price ($)">
            <Input type="number" min="0" step="0.01" value={damageProtectionPrice} onChange={(e) => setDamageProtectionPrice(e.target.value)} data-testid="damage-protection-price-input" />
          </Field>
        </div>
        </div>
      </Card>

      {/* ---------- ENCHANTED CARE ---------- */}
      <Card>
        <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldPlus className="w-4 h-4 text-[#FFD166]" />
          <h2 className="font-serif text-xl text-[#FFD166]">Enchanted Care</h2>
        </div>
        <p className="text-xs text-[#B9C8D5] mb-4">
          Non-refundable damage waiver. Charged as <b>max(minimum, subtotal × %)</b> and always kept — even when the booking is cancelled or refunded via Peace of Mind.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Percent of subtotal (%)">
            <Input type="number" min="0" step="0.1" value={carePercent} onChange={(e) => setCarePercent(e.target.value)} data-testid="enchanted-care-percent-input" />
          </Field>
          <Field label="Minimum ($)">
            <Input type="number" min="0" step="0.01" value={careMinimum} onChange={(e) => setCareMinimum(e.target.value)} data-testid="enchanted-care-minimum-input" />
          </Field>
        </div>
        <p className="text-[11px] text-[#B9C8D5]/70 mt-2">
          Example: {carePercent}% of a $80 subtotal = ${(80 * Number(carePercent) / 100).toFixed(2)} → clamped to a ${Number(careMinimum).toFixed(2)} minimum.
        </p>
        </div>
      </Card>

      {/* ---------- ENCHANTED PEACE OF MIND ---------- */}
      <Card>
        <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldPlus className="w-4 h-4 text-[#FFD166]" />
          <h2 className="font-serif text-xl text-[#FFD166]">Enchanted Peace of Mind</h2>
        </div>
        <p className="text-xs text-[#B9C8D5] mb-4">
          Cancel-anytime coverage. Only offered when the customer books at least <b>N days</b> ahead of delivery. Enchanted Care is always kept when a PoM refund fires.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Percent of subtotal (%)">
            <Input type="number" min="0" step="0.1" value={pomPercent} onChange={(e) => setPomPercent(e.target.value)} data-testid="enchanted-pom-percent-input" />
          </Field>
          <Field label="Booking-lead-time gate (days)">
            <Input type="number" min="0" step="1" value={pomMinDaysOut} onChange={(e) => setPomMinDaysOut(e.target.value)} data-testid="enchanted-pom-days-input" />
          </Field>
        </div>
        <p className="text-[11px] text-[#B9C8D5]/70 mt-2">
          Example: {pomPercent}% of a $80 subtotal = ${(80 * Number(pomPercent) / 100).toFixed(2)}. Customers booking within {pomMinDaysOut} days of delivery won't see the checkbox.
        </p>
        </div>
      </Card>

      {/* ---------- BUILD YOUR OWN BUNDLE ---------- */}
      <Card>
        <div className="p-5" data-testid="byob-settings-card">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Gift className="w-4 h-4 text-[#FFD166]" />
            <h2 className="font-serif text-xl text-[#FFD166]">Build-Your-Own Baby Gear Bundle</h2>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-[#F6F1E4]" data-testid="byob-enabled-toggle">
            <input type="checkbox" checked={byobEnabled} onChange={(e) => setByobEnabled(e.target.checked)} className="accent-[#FFD166] w-4 h-4" data-testid="byob-enabled-input" />
            {byobEnabled ? "On — live on the website" : "Off"}
          </label>
        </div>
        <p className="text-xs text-[#B9C8D5] mb-4">
          Customers who add <b>{byobMinUnits || 3}+ eligible baby items</b> (cribs, monitors, high chairs…) automatically save <b>{byobPercent || 0}%</b> on those items.
          Strollers, ECVs and curated bundles are never eligible. Stacks with promo codes. Pick eligible categories here; override any single product from its edit form.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Discount on eligible items (%)">
            <Input type="number" min="0" max="100" step="0.5" value={byobPercent} onChange={(e) => setByobPercent(e.target.value)} data-testid="byob-percent-input" />
          </Field>
          <Field label="Minimum eligible units in cart">
            <Input type="number" min="1" step="1" value={byobMinUnits} onChange={(e) => setByobMinUnits(e.target.value)} data-testid="byob-min-units-input" />
          </Field>
        </div>
        <div className="mt-4">
          <div className="text-xs uppercase tracking-[0.2em] text-[#B9C8D5] mb-2">Eligible categories (default for every product inside)</div>
          <div className="flex flex-wrap gap-2" data-testid="byob-category-chips">
            {categories.filter((c) => !["strollers", "ecvs", "bundles"].includes(c.id)).map((c) => {
              const on = byobCategoryIds.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setByobCategoryIds((cur) => (on ? cur.filter((x) => x !== c.id) : [...cur, c.id]))}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#FFD166] text-[#1a0a03] border-[#FFD166]" : "border-[#FFD166]/25 text-[#B9C8D5] hover:border-[#FFD166]/60"}`}
                  data-testid={`byob-category-${c.id}`}
                >
                  {c.name}
                </button>
              );
            })}
            {categories.length === 0 && <span className="text-xs text-[#B9C8D5]/60 italic">No categories yet.</span>}
          </div>
          <p className="text-[11px] text-[#B9C8D5]/70 mt-2">Strollers, ECVs and Bundles are excluded by design and can't be selected.</p>
        </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Btn onClick={save} disabled={saving || !canSave} data-testid="settings-save-btn-bottom">
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
        </Btn>
      </div>

      {/* ---------- MAINTENANCE ---------- */}
      <Card>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Brush className="w-4 h-4 text-[#FFD166]" />
            <h2 className="font-serif text-xl text-[#FFD166]">Maintenance</h2>
          </div>
          <p className="text-xs text-[#B9C8D5] mb-4">
            Nightly at 3 AM ET, we cancel unpaid holds older than 48 hours so their inventory frees up.
            You can also run it manually right now — safe to click any time.
          </p>
          <Btn onClick={runJanitor} disabled={janitorRunning} data-testid="pending-janitor-run-btn" variant="ghost">
            {janitorRunning ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Cleaning…</> : <><Brush className="w-3.5 h-3.5" /> Clean up stale pending holds now</>}
          </Btn>
          {janitorResult && (
            <div className="mt-3 text-xs text-[#B9C8D5]" data-testid="pending-janitor-result">
              <span className="text-[#F6F1E4]">{janitorResult.cancelled_count}</span> reservation(s) cancelled ·
              <span className="text-[#F6F1E4]"> {janitorResult.freed_units}</span> unit(s) freed ·
              cutoff = <span className="font-mono">{janitorResult.cutoff_iso?.slice(0, 16).replace("T", " ")} UTC</span>
            </div>
          )}
        </div>
      </Card>

      {/* Finance / Tax Reserves */}
      <Card>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-[#FFD166]" />
            <h2 className="font-serif text-xl text-[#FFD166]">Finance / Tax Reserves</h2>
          </div>
          <p className="text-xs text-[#B9C8D5] mb-4">
            These rates are used by the Payouts waterfall to hold back reserves from each sweep. They do not affect when Stripe releases funds — only how much of the eligible amount is available for bank transfer.
          </p>

          {/* Payout Sweep Gate */}
          <div className="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-amber-300">Live Payout Sweep</div>
                <div className="text-[11px] text-[#B9C8D5] mt-0.5">
                  {payoutSweepEnabled
                    ? "Enabled — nightly sweep and Run Sweep Now will move money to your bank."
                    : "Paused (fail-closed) — no money will leave Stripe automatically or manually."}
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  checked={payoutSweepEnabled}
                  onChange={(e) => setPayoutSweepEnabled(e.target.checked)}
                  className="sr-only peer"
                  data-testid="settings-payout-sweep-enabled"
                />
                <div className="w-11 h-6 bg-black/40 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all border border-[#B9C8D5]/30 peer-checked:bg-amber-500/60" />
              </label>
            </div>
            {!payoutSweepEnabled && (
              <div className="text-[11px] text-amber-200/70 border-t border-amber-500/20 pt-2">
                Before enabling: review the Reconciliation Report on the Payouts page and confirm the Stripe balance is fully explained by payout schedule entries. Enabling this allows real money to be transferred.
              </div>
            )}
            {payoutSweepEnabled && (
              <div className="text-[11px] text-amber-200/70 border-t border-amber-500/20 pt-2">
                Note: The sweep will still be blocked if there is a Reconciliation Imbalance (&gt;$2 unattributed Stripe balance) or any unresolved Needs Review flags.
              </div>
            )}
          </div>

          <div className="max-w-xs">
            <label className="block mb-1 text-sm text-[#B9C8D5]">Federal tax reserve rate (%)</label>
            <Input
              type="number"
              min={0}
              max={60}
              step={1}
              value={federalTaxReservePercent}
              onChange={(e) => setFederalTaxReservePercent(Number(e.target.value))}
              data-testid="settings-federal-reserve-percent"
            />
            <p className="text-[11px] text-[#B9C8D5] mt-1">
              Estimated SE tax (~15.3%) + income tax bracket. Default 25%. Applied to the net-of-sales-tax eligible balance on the Payouts page. This is an internal cash-management reserve — separate from FL sales tax.
            </p>
          </div>
        </div>
      </Card>

      {/* Admin Notifications */}
      <Card className="overflow-hidden">
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Save className="w-4 h-4 text-[#FFD166]" />
            <h2 className="font-serif text-xl text-[#FFD166]">Admin Notifications</h2>
          </div>
          <p className="text-xs text-[#B9C8D5] mb-4">
            Admin-specific notification emails are sent <strong>separately</strong> from customer emails.
            Customer emails are never CC'd or BCC'd to the admin address.
          </p>
          <div className="space-y-4">
            {/* ── Email notifications ── */}
            <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-[#FFD166]/15 bg-black/20">
              <input
                type="checkbox"
                checked={adminEmailEnabled}
                onChange={(e) => setAdminEmailEnabled(e.target.checked)}
                className="accent-[#FFD166]"
                data-testid="settings-admin-email-enabled"
              />
              <div>
                <div className="text-sm font-medium">Admin email alerts enabled</div>
                <div className="text-xs text-[#B9C8D5]">Send a "new booking" alert to each address below when a booking is paid.</div>
              </div>
            </label>

            {/* Multi-value email chips */}
            <div>
              <label className="eyebrow block mb-2">Notification Emails</label>
              {adminEmails.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {adminEmails.map((email) => (
                    <span key={email} className="inline-flex items-center gap-1.5 bg-[#FFD166]/15 text-[#FFD166] px-2.5 py-1 rounded-full text-xs font-medium border border-[#FFD166]/20">
                      {email}
                      <button
                        type="button"
                        onClick={() => setAdminEmails(adminEmails.filter((e) => e !== email))}
                        className="hover:text-red-400 transition-colors leading-none"
                        data-testid={`settings-remove-email-${email.replace(/[@.]/g, "_")}`}
                      >×</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const v = newAdminEmail.trim();
                      if (!v) return;
                      if (adminEmails.includes(v)) { toast.error("Already added"); return; }
                      setAdminEmails([...adminEmails, v]);
                      setNewAdminEmail("");
                    }
                  }}
                  placeholder="info@enchantedgearrentals.com"
                  data-testid="settings-new-admin-email"
                />
                <button
                  type="button"
                  onClick={() => {
                    const v = newAdminEmail.trim();
                    if (!v) return;
                    if (adminEmails.includes(v)) { toast.error("Already added"); return; }
                    setAdminEmails([...adminEmails, v]);
                    setNewAdminEmail("");
                  }}
                  className="px-4 py-2 border border-[#FFD166]/30 text-[#FFD166] rounded-lg text-sm hover:bg-[#FFD166]/10 transition-colors whitespace-nowrap"
                  data-testid="settings-add-admin-email"
                >+ Add</button>
              </div>
              <p className="text-xs text-[#B9C8D5] mt-1">Press Enter or click Add. Each address gets its own email.</p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={sendTestEmail}
                disabled={testEmailBusy || adminEmails.length === 0}
                className="text-sm border border-[#FFD166]/30 text-[#FFD166] px-4 py-2 rounded-lg hover:bg-[#FFD166]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="settings-send-test-email"
              >
                {testEmailBusy ? "Sending…" : `Send test email${adminEmails.length > 1 ? ` (${adminEmails.length})` : ""}`}
              </button>
              <span className="text-xs text-[#B9C8D5]">Sends a test to all addresses above.</span>
            </div>

            {/* ── SMS notifications ── */}
            <div className="border-t border-[#FFD166]/10 pt-4 space-y-3">
              <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-[#FFD166]/15 bg-black/20">
                <input
                  type="checkbox"
                  checked={adminSmsEnabled}
                  onChange={(e) => setAdminSmsEnabled(e.target.checked)}
                  className="accent-[#FFD166]"
                  data-testid="settings-admin-sms-enabled"
                />
                <div>
                  <div className="text-sm font-medium">Admin SMS alerts enabled</div>
                  <div className="text-xs text-[#B9C8D5]">Text all numbers below when a booking is paid. Requires Twilio.</div>
                </div>
              </label>

              {/* Multi-value phone chips */}
              <div>
                <label className="eyebrow block mb-2">Notification Phone Numbers</label>
                {adminPhones.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {adminPhones.map((phone) => (
                      <span key={phone} className="inline-flex items-center gap-1.5 bg-[#FFD166]/15 text-[#FFD166] px-2.5 py-1 rounded-full text-xs font-medium border border-[#FFD166]/20">
                        {phone}
                        <button
                          type="button"
                          onClick={() => setAdminPhones(adminPhones.filter((p) => p !== phone))}
                          className="hover:text-red-400 transition-colors leading-none"
                          data-testid={`settings-remove-phone-${phone.replace(/\+/g, "")}`}
                        >×</button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    type="tel"
                    value={newAdminPhone}
                    onChange={(e) => setNewAdminPhone(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const v = newAdminPhone.trim();
                        if (!v) return;
                        if (adminPhones.includes(v)) { toast.error("Already added"); return; }
                        setAdminPhones([...adminPhones, v]);
                        setNewAdminPhone("");
                      }
                    }}
                    placeholder="+14075550100"
                    data-testid="settings-new-admin-phone"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const v = newAdminPhone.trim();
                      if (!v) return;
                      if (adminPhones.includes(v)) { toast.error("Already added"); return; }
                      setAdminPhones([...adminPhones, v]);
                      setNewAdminPhone("");
                    }}
                    className="px-4 py-2 border border-[#FFD166]/30 text-[#FFD166] rounded-lg text-sm hover:bg-[#FFD166]/10 transition-colors whitespace-nowrap"
                    data-testid="settings-add-admin-phone"
                  >+ Add</button>
                </div>
                <p className="text-xs text-[#B9C8D5] mt-1">E.164 format: +14075550100. Press Enter or click Add.</p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={sendTestSms}
                  disabled={testSmsBusy || adminPhones.length === 0}
                  className="text-sm border border-[#FFD166]/30 text-[#FFD166] px-4 py-2 rounded-lg hover:bg-[#FFD166]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="settings-send-test-sms"
                >
                  {testSmsBusy ? "Sending…" : `Send test SMS${adminPhones.length > 1 ? ` (${adminPhones.length})` : ""}`}
                </button>
                <span className="text-xs text-[#B9C8D5]">Sends a test text to all numbers above.</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* ── Customer Reviews ─────────────────────────────────────────────────── */}
      <Card data-testid="settings-reviews-card">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-[#FFD166]" />
            <h2 className="font-serif text-xl text-[#FFD166]">Customer Reviews</h2>
          </div>
          <p className="text-xs text-[#B9C8D5]">
            Enchanted automatically emails guests a review request the day after their gear is picked up.
            Paste your Google review link below to add a direct "Leave a review ★★★★★" button to those emails.
          </p>
          <div>
            <label className="text-xs text-[#B9C8D5] block mb-1">Google Review URL</label>
            <div className="flex gap-2">
              <Input
                value={reviewUrl}
                onChange={(e) => setReviewUrl(e.target.value)}
                placeholder="https://g.page/r/CabcXYZ123/review"
                data-testid="settings-review-url"
                className="flex-1"
              />
              <button
                type="button"
                onClick={saveReviewUrl}
                disabled={savingReviewUrl}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FFD166] text-[#040A0F] font-medium text-sm hover:bg-[#FFC933] disabled:opacity-50 transition-colors whitespace-nowrap"
                data-testid="settings-save-review-url"
              >
                <Save className="w-3.5 h-3.5" />
                {savingReviewUrl ? "Saving…" : "Save"}
              </button>
            </div>
            <p className="text-xs text-[#B9C8D5] mt-1.5">
              Find your link in Google Maps → Your Business → Get more reviews → copy the short URL.
              Leave blank to show a friendly placeholder in the review email.
            </p>
          </div>
          {reviewUrl && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/8 px-4 py-3 text-xs text-emerald-300" data-testid="settings-review-url-preview">
              Review button will link to: <a href={reviewUrl} target="_blank" rel="noreferrer" className="underline break-all">{reviewUrl}</a>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
